import { Injectable } from "@angular/core";
import {
	AutoCoverOptions, ICustomerModel, IAutoCoverSpecModel, ILathamOrderModel, ILocationModel, IServiceAreaModel,
	LineItemModel, LineItemTypes, formatAutoCoverInches
} from "../models";
import { GlobalsService } from "./utils/globals.service";
import { LookupService } from "./utils/lookup.service";

// Carries the intent to quote a replacement cover from an Auto Cover spec sheet across the route
// change from the job (or the mobile wizard) to the Latham Orders page. Registered at the root so it
// stays a singleton even though most Latham services are provided per-component - same pattern as
// LathamOrderFromClaimService.
export interface IAutoCoverQuoteRequest {
	jobId: number;
	equipmentTrackingId: number;
	spec: IAutoCoverSpecModel;
	customerName?: string;
	// The job's site customer, which is the home owner of the pool the cover is being quoted for.
	// Resolved by the launcher because it is the only side that knows the job.
	homeOwner?: ICustomerModel;
}

// The Auto Cover spec sheet uses the words a tech reads off the equipment; a Latham order uses its
// own spec vocabulary. Where a recorded value has an exact Latham equivalent it's translated here.
// Where it doesn't, the order field is deliberately left for the office to choose and the recorded
// value is written into the office notes instead - a near-miss guess would put an invalid value into
// a real order and quietly mis-price it.
const MANUFACTURER_TO_OEM: Record<string, string> = {
	"Coverstar": "Coverstar",
	"Pool Cover Specialist": "Pool Cover Specialists",
	"Coverpools": "Cover Pools II",
	"APC": "APC",
	"Aquamatic": "Aquamatic"
	// "Other" has no Latham OEM
};

const UNIT_LOCATION_TO_LATHAM: Record<string, string> = {
	"Recessed": "Recessed",
	"Deck Mount": "Deck"
};

const MOTOR_TYPE_TO_LATHAM: Record<string, string> = {
	"Electric": "Electric Std",
	"Electric HD": "Electric HD"
	// "Hydraulic" is one word on the sheet but two products on an order (US Hydro / European Hydro)
};

const SWITCH_TO_CONTROL_TYPE: Record<string, string> = {
	"Key": "Standard Keyswitch",
	"Touchpad": "Touch Screen"
	// "Toggle" is not Latham's "Rocker Switch"
};

const WEBBING_TO_LATHAM: Record<string, string> = {
	"Heat Sealed": "Heat Sealed Sewn",
	"Sewn": "Sewn"
};

const ROPE_ATTACHMENT_TO_LATHAM: Record<string, string> = {
	"Continuous": "Continuous",
	"Continuous w/ Tab": "Continuous w/tab",
	"Detachable": "Detachable"
	// "Steel cable" has no Latham equivalent
};

// Leading edge bar profiles. Only the 3" bar is renamed: the order's dropdown calls it
// "3 Inch Standard" for every cover model type, and plain "3 Inch" only on Replacement Fabric.
// Everything else is spelled identically on both sides and falls through unchanged.
const LE_SIZE_TO_LATHAM: Record<string, string> = {
	"3 Inch": "3 Inch Standard"
};

// The dropdown shows "403 Undertrack" but its value is "Undertrack" - the two are the same track.
// Only "Toptrack" matters to pricing beyond the track itself: sliders are not added on a top track.
const TRACK_TYPE_TO_LATHAM: Record<string, string> = {
	"Undertrack": "Undertrack",
	"Top Track": "Toptrack"
};

// A replacement cover is its own model type on an order, and nothing prices until one is chosen:
// calculateSystemLineItems returns early while system.modelType is empty, which is why a quote built
// from these specs used to open with no line items at all.
//
// Each brand sells the same thing under its own name - Coverstar as CS500 ("CS500 - Replacement
// Cover"), PCS as "Replacement Fabric" - and the brand is the company's, not the equipment's:
// getNewLathamOrder() has already set it from company.orderingType before this runs. The mechanism
// the new cover has to fit is carried by cover.oem in either case.
const REPLACEMENT_COVER_MODEL_TYPE_BY_BRAND: Record<string, string> = {
	"Coverstar": "CS500",
	"PCS": "Replacement Fabric"
};

// Used only when the company's ordering type set no brand at all
const DEFAULT_REPLACEMENT_COVER_BRAND: string = "Coverstar";

// The brand a replacement quote raised from the tracker opens as: the company's ordering type, which
// getNewLathamOrder() copies onto every new order, else the default applySpecsToOrder() falls back to.
// The spec sheet uses it to show the slack that quote will calculate.
export function autoCoverReplacementBrand(): string {
	const orderingType = GlobalsService.company?.orderingType;
	return (orderingType === "Coverstar" || orderingType === "PCS") ? orderingType : DEFAULT_REPLACEMENT_COVER_BRAND;
}

// The tracker spells this correctly; the Latham dropdown carries a long-standing typo.
const SLIDER_TO_LATHAM: Record<string, string> = {
	"Residential UG": "Residental UG"
};

// A replacement cover quoted off the tracker is picked up at the shop and taken back out by the crew
// that measured it, so it ships to the company and goes out will call. The ship to value is the
// dropdown's own id; the carrier is a per-company lookup row matched on its name, since its id is
// whatever that company's carrier list happens to use.
const DEFAULT_SHIP_TO: string = "Company";
const DEFAULT_CARRIER_NAME: string = "willcall";

// Reading an order back onto a spec sheet is the same translation run backwards, so the reverse
// lookups are derived from the forward ones rather than typed out again - two hand-maintained lists
// would drift, and a wrong reverse entry writes a value the spec sheet's dropdown doesn't offer.
//
// Every forward map is 1:1 today. Where one ever isn't, the last key wins, which is why the maps
// above are the single source of truth for both directions.
const invert = (map: Record<string, string>): Record<string, string> => {
	const inverted: Record<string, string> = {};
	Object.keys(map).forEach(key => inverted[map[key]] = key);
	return inverted;
};

const OEM_TO_MANUFACTURER: Record<string, string> = invert(MANUFACTURER_TO_OEM);
const LATHAM_TO_UNIT_LOCATION: Record<string, string> = invert(UNIT_LOCATION_TO_LATHAM);
const LATHAM_TO_MOTOR_TYPE: Record<string, string> = invert(MOTOR_TYPE_TO_LATHAM);
const CONTROL_TYPE_TO_SWITCH: Record<string, string> = invert(SWITCH_TO_CONTROL_TYPE);
const LATHAM_TO_WEBBING: Record<string, string> = invert(WEBBING_TO_LATHAM);
const LATHAM_TO_ROPE_ATTACHMENT: Record<string, string> = invert(ROPE_ATTACHMENT_TO_LATHAM);
const LATHAM_TO_LE_SIZE: Record<string, string> = invert(LE_SIZE_TO_LATHAM);
const LATHAM_TO_TRACK_TYPE: Record<string, string> = invert(TRACK_TYPE_TO_LATHAM);
const LATHAM_TO_SLIDER: Record<string, string> = invert(SLIDER_TO_LATHAM);

@Injectable({ providedIn: "root" })
export class AutoCoverToLathamOrderService {
	private _pendingRequest: IAutoCoverQuoteRequest | null = null;

	// Which spec sheet the quote about to be opened came from. Outlives the request itself because the
	// order has no id to link back with until it's saved for the first time.
	private _pendingLinkEquipmentTrackingId: number = 0;

	// A quote raised off the tracker saves itself once it has opened and priced, so the office finds a
	// real quote waiting rather than an unsaved draft that's lost if the page is left. Outlives the
	// request for the same reason the link above does: the save happens in the order editor, a screen
	// and a route change later than the request is consumed.
	private _pendingAutoSave: boolean = false;

	constructor(private readonly lookupService: LookupService) { }

	get hasPendingRequest(): boolean {
		return this._pendingRequest != null;
	}

	setPendingRequest(request: IAutoCoverQuoteRequest): void {
		this._pendingRequest = request;
	}

	// Returns the pending request (if any) and clears it so it's only honored once.
	consumePendingRequest(): IAutoCoverQuoteRequest | null {
		const request = this._pendingRequest;
		this._pendingRequest = null;

		if (request?.equipmentTrackingId > 0)
			this._pendingLinkEquipmentTrackingId = request.equipmentTrackingId;

		if (request)
			this._pendingAutoSave = true;

		return request;
	}

	// Consumed by the first successful save of the order that was opened from those specs.
	consumePendingLink(): number {
		const equipmentTrackingId = this._pendingLinkEquipmentTrackingId;
		this._pendingLinkEquipmentTrackingId = 0;
		return equipmentTrackingId;
	}

	// Consumed by the order editor once the quote is on screen. Only ever true once per quote, so a
	// failed auto save isn't retried behind the user's back - they still have the Save button.
	consumePendingAutoSave(): boolean {
		const autoSave = this._pendingAutoSave;
		this._pendingAutoSave = false;
		return autoSave;
	}

	// The pool the cover is being quoted for belongs to the job's site customer, so that customer is
	// the order's home owner. Set here rather than left for the office to search out again, and set
	// the way the home owner search would have set it - the customer's standing Latham notes come
	// along with them (see LathamOrderHomeOwnerComponent.onHomeOwnerChange).
	//
	// Nothing else has to be done for billing: with billToOverride still false, the bill-to section
	// defaults itself to Home Owner as soon as the order renders.
	applyHomeOwnerToOrder(lathamOrderModel: ILathamOrderModel, homeOwner: ICustomerModel): ILathamOrderModel {
		if (!lathamOrderModel || !homeOwner?.customerId)
			return lathamOrderModel;

		lathamOrderModel.orderSpecs.homeOwner = homeOwner;

		if (homeOwner.lathamDefaultNotes)
			lathamOrderModel.orderNotes = lathamOrderModel.orderNotes
				? `${lathamOrderModel.orderNotes}\n\n${homeOwner.lathamDefaultNotes}`
				: homeOwner.lathamDefaultNotes;

		return lathamOrderModel;
	}

	// A new order opens with no ship to and no carrier at all, so these are defaults rather than
	// overrides: the crew that measured the pool picks the cover up and takes it back out.
	//
	// Ship to is only set where the ship to dropdown actually offers "Company" - it's left off the
	// list for a child company (which ships to its parent's customers) and for PTW (which ships to
	// saved addresses only), and setting a value that isn't in the list would leave the dropdown
	// looking empty with no way to tell what it's shipping to.
	//
	// Carrier is deliberately matched by name: the ids in LathamCarriers are per company, so there is
	// no fixed "will call" id to key off. If a company doesn't carry one, the carrier is left for the
	// office to choose.
	applyShippingDefaultsToOrder(lathamOrderModel: ILathamOrderModel): ILathamOrderModel {
		if (!lathamOrderModel)
			return lathamOrderModel;

		const shipping = lathamOrderModel.orderSpecs.shipping;

		const companyIsShipTo = !GlobalsService.company.isPavinTheWay && !GlobalsService.company.parentCompanyId;
		if (companyIsShipTo && !shipping.shipTo)
			shipping.shipTo = DEFAULT_SHIP_TO;

		if (!shipping.carrier) {
			const willCall = (this.lookupService.getLathamCarriers() || [])
				.find(x => (x.name || "").replace(/[^a-z]/gi, "").toLowerCase() === DEFAULT_CARRIER_NAME);

			if (willCall)
				shipping.carrier = willCall.lathamCarrierId;
		}

		return lathamOrderModel;
	}

	// Puts the labor for the customer's area on the quote, worked out the way the office would: the home
	// owner's zip (or city) puts them in a service area, the service area belongs to a department, and
	// each department picks the labor it charges on a replacement cover (Settings > Locations >
	// Departments). A customer outside every service area gets the main department's choice.
	//
	// Does nothing until a department has a labor picked, so companies that haven't set it up see no
	// change. Departments, service areas and labor items are all cached lookups, so on a phone this costs
	// no extra requests.
	applyReplacementCoverLaborToOrder(lathamOrderModel: ILathamOrderModel, homeOwner: ICustomerModel): ILathamOrderModel {
		if (!lathamOrderModel?.invoice)
			return lathamOrderModel;

		const departments = (this.lookupService.getLocations() || []).filter(x => x.replacementCoverLaborItemId > 0);
		if (departments.length === 0)
			return lathamOrderModel;

		const serviceArea = this.findServiceArea(homeOwner);
		const areaDepartmentIds = (serviceArea?.serviceAreaLocations || []).map(x => x.locationId);

		const department: ILocationModel = departments.find(x => areaDepartmentIds.indexOf(x.locationId) >= 0)
			?? departments.find(x => x.isMain);

		const laborItem = department
			? (this.lookupService.getInvoiceLaborItems() || []).find(x => x.invoiceLaborItemId === department.replacementCoverLaborItemId)
			: null;

		if (!laborItem) {
			lathamOrderModel.officeNotes = this.appendNote(lathamOrderModel.officeNotes,
				serviceArea
					? `No replacement cover labor is set up for the ${serviceArea.name} service area. Labor was left for the office to choose.`
					: "The customer's zip isn't in a service area with replacement cover labor set up. Labor was left for the office to choose.");
			return lathamOrderModel;
		}

		// The same values picking the labor by hand sets (LineItemsBaseComponent.onLaborSelected), on the
		// Labor 1 row the line items default to having anyway
		let labor = lathamOrderModel.invoice.lineItems.find(x => x.lineItemType === LineItemTypes.Labor);
		if (!labor) {
			labor = new LineItemModel(LineItemTypes.Labor);
			labor.cost = 0;
			labor.taxable = false;
			labor.editable = false;
			lathamOrderModel.invoice.lineItems.push(labor);
		}

		labor.laborItemId = laborItem.invoiceLaborItemId;
		labor.sku = laborItem.sku;
		labor.description = laborItem.description;
		labor.price = laborItem.price;
		labor.quantity = 1;

		const where = serviceArea ? `the ${serviceArea.name} service area (${department.name})` : `${department.name}, the main department`;
		lathamOrderModel.officeNotes = this.appendNote(lathamOrderModel.officeNotes, `Labor ${laborItem.sku} added for ${where}.`);

		return lathamOrderModel;
	}

	// Zip first, since it's the more specific of the two and what service areas are mostly set up by,
	// then city. ZIP+4 matches its five digit zip, and postal codes match regardless of case or spacing.
	private findServiceArea(homeOwner: ICustomerModel): IServiceAreaModel {
		const serviceAreas = this.lookupService.getServiceAreas() || [];
		const normalize = (value: string) => (value || "").toUpperCase().replace(/\s+/g, "");
		const list = (value: string) => (value || "").split(",").map(x => normalize(x)).filter(x => !!x);

		const zip = normalize(homeOwner?.zipcode);
		if (zip) {
			const zip5 = /^\d{5}-?\d{4}$/.test(zip) ? zip.substring(0, 5) : zip;
			const byZip = serviceAreas.find(x => list(x.zipcodes).some(z => z === zip || z === zip5));
			if (byZip)
				return byZip;
		}

		const city = normalize(homeOwner?.city);
		if (city)
			return serviceAreas.find(x => list(x.cityNames).indexOf(city) >= 0) ?? null;

		return null;
	}

	// A blank line first, so the note doesn't read as one more entry in the list buildOfficeNotes ends with
	private appendNote(notes: string, line: string): string {
		return notes ? `${notes}\n\n${line}` : line;
	}

	// The other direction: fills a spec sheet in from an existing Latham order, for when the cover on
	// the pool is one this company already built and the order is a better record of it than anything
	// a tech could read off the equipment.
	//
	// Only writes what the order actually carries, so this tops up a part-filled sheet rather than
	// blanking the fields an order has nothing to say about. Everything an order has no field for -
	// the mechanism model, lid and housing, guide feeds, the offsets - is left exactly as it was.
	//
	// Returns a new object: the spec sheet form refreshes its manufacturer-dependent option lists off
	// ngOnChanges, which a mutation in place would never fire.
	applyOrderToSpecs(spec: IAutoCoverSpecModel, lathamOrderModel: ILathamOrderModel): IAutoCoverSpecModel {
		if (!spec || !lathamOrderModel?.orderSpecs)
			return spec;

		const filled: IAutoCoverSpecModel = { ...spec };
		const specs = lathamOrderModel.orderSpecs;

		const apply = (value: string, set: (translated: string) => void) => {
			if (value)
				set(value);
		};

		// Mechanism
		apply(OEM_TO_MANUFACTURER[specs.cover?.oem], v => filled.manufacturer = v);
		apply(LATHAM_TO_UNIT_LOCATION[specs.system?.unitLocation], v => filled.unitLocation = v);
		apply(specs.system?.poolType, v => filled.poolType = v);
		apply(specs.system?.motorSide, v => filled.motorSide = v);
		apply(LATHAM_TO_MOTOR_TYPE[specs.system?.motorType], v => filled.motorType = v);
		apply(CONTROL_TYPE_TO_SWITCH[specs.system?.controlType], v => filled.switchType = v);

		// Fabric
		apply(specs.cover?.fabricColor, v => filled.fabricColor = v);
		apply(LATHAM_TO_WEBBING[specs.cover?.webbingType], v => filled.webbingType = v);
		apply(LATHAM_TO_ROPE_ATTACHMENT[specs.cover?.ropeAttachment], v => filled.ropeAttachment = v);
		apply(specs.cover?.leadingEdgeAttachment, v => filled.leAttachment = v);
		apply(LATHAM_TO_TRACK_TYPE[specs.track?.trackType], v => filled.trackType = v);

		// The 3" bar is the only renamed profile, so anything with no reverse entry is already spelled
		// the way the spec sheet spells it.
		apply(LATHAM_TO_LE_SIZE[specs.tubes?.leadingEdge] ?? specs.tubes?.leadingEdge, v => filled.leSize = v);
		apply(LATHAM_TO_SLIDER[specs.cover?.sliderType] ?? specs.cover?.sliderType, v => filled.sliders = v);

		// Measurements
		if (specs.system?.trackSpaceFeet != null) filled.widthFeet = specs.system.trackSpaceFeet;
		if (specs.system?.trackSpaceInches != null) filled.widthInches = specs.system.trackSpaceInches;
		if (specs.system?.trackLengthFeet != null) filled.lengthFeet = specs.system.trackLengthFeet;
		if (specs.system?.trackLengthInches != null) filled.lengthInches = specs.system.trackLengthInches;
		// The sheet only stores a slack someone overrode, and shows the standard one otherwise, so only
		// an order's overridden slack is carried across
		if (specs.cover?.slack != null && specs.cover.slackOverride) filled.slack = specs.cover.slack;

		// A model recorded against the old manufacturer isn't offered under the new one, and leaving it
		// would show a dropdown with a value that isn't in its own list.
		if (filled.manufacturer !== spec.manufacturer &&
			!AutoCoverOptions.modelsForManufacturer(filled.manufacturer).some(x => x.id === filled.model))
			filled.model = null;

		// The sheet now describes that order's cover, so tie the two together.
		if (lathamOrderModel.lathamOrderId > 0)
			filled.lathamOrderId = lathamOrderModel.lathamOrderId;

		return filled;
	}

	// Copies everything the tech recorded onto a brand new order's specs, as a replacement cover, so
	// the order opens priced instead of empty.
	//
	// Note the mechanism the tech recorded (Eclipse, CS3000, T4, ...) is NOT the order's model type.
	// It describes the system the new cover has to fit, and belongs in cover.oem; the order's model
	// type describes what is being sold, which here is always a replacement cover.
	applySpecsToOrder(lathamOrderModel: ILathamOrderModel, spec: IAutoCoverSpecModel): ILathamOrderModel {
		if (!lathamOrderModel || !spec)
			return lathamOrderModel;

		const specs = lathamOrderModel.orderSpecs;
		const carried: string[] = [];
		const notCarried: string[] = [];

		// Set before anything else: every pick list below is keyed off brand and model, and the line
		// item engine does nothing until they are set. cover.fabricType is deliberately left empty so
		// the fabric type component fills in the brand's default the first time the order renders.
		const brandType = specs.system.brandType || DEFAULT_REPLACEMENT_COVER_BRAND;
		specs.system.brandType = brandType;
		specs.system.modelType = REPLACEMENT_COVER_MODEL_TYPE_BY_BRAND[brandType] ?? specs.system.modelType;

		// Records what happened to one field so the office notes can say which values made it onto the
		// order and which are sitting in the notes waiting for a decision.
		const apply = (label: string, recorded: string, translated: string, set: (value: string) => void) => {
			if (!recorded)
				return;

			if (translated) {
				set(translated);
				carried.push(label);
			}
			else {
				notCarried.push(`${label}: ${recorded}`);
			}
		};

		// System
		apply("Manufacturer", spec.manufacturer, MANUFACTURER_TO_OEM[spec.manufacturer], v => specs.cover.oem = v);
		apply("Unit Location", spec.unitLocation, UNIT_LOCATION_TO_LATHAM[spec.unitLocation], v => specs.system.unitLocation = v);
		apply("Pool Type", spec.poolType, spec.poolType, v => specs.system.poolType = v);
		apply("Motor Side", spec.motorSide, spec.motorSide, v => specs.system.motorSide = v);
		apply("Motor Type", spec.motorType, MOTOR_TYPE_TO_LATHAM[spec.motorType], v => specs.system.motorType = v);
		apply("Switch", spec.switchType, SWITCH_TO_CONTROL_TYPE[spec.switchType], v => specs.system.controlType = v);

		// Cover
		apply("Fabric Color", spec.fabricColor, spec.fabricColor, v => specs.cover.fabricColor = v);
		apply("Webbing Type", spec.webbingType, WEBBING_TO_LATHAM[spec.webbingType], v => specs.cover.webbingType = v);
		apply("Rope Attachment", spec.ropeAttachment, ROPE_ATTACHMENT_TO_LATHAM[spec.ropeAttachment], v => specs.cover.ropeAttachment = v);
		apply("Leading Edge Attachment", spec.leAttachment, spec.leAttachment, v => specs.cover.leadingEdgeAttachment = v);

		// Tubes > Leading Edge. The tracker's sizes are the same physical descriptions the order's
		// dropdown uses, so most pass straight through; the exception is a 3" bar, which every cover
		// model type calls "3 Inch Standard" (only the Replacement Fabric list calls it "3 Inch").
		apply("Leading Edge Size", spec.leSize, LE_SIZE_TO_LATHAM[spec.leSize] ?? spec.leSize, v => specs.tubes.leadingEdge = v);
		apply("Sliders", spec.sliders, SLIDER_TO_LATHAM[spec.sliders] ?? spec.sliders, v => specs.cover.sliderType = v);

		// Track Type is set even though the Track section is hidden on a cover order
		// ([hidden]="!(isSystem && !isCover)"), because the line item engine still reads it: sliders
		// are skipped when it is "Toptrack". Leaving it empty would add sliders to every top track
		// quote, and the office has no field to correct it in.
		apply("Track Type", spec.trackType, TRACK_TYPE_TO_LATHAM[spec.trackType], v => specs.track.trackType = v);

		// Lid Type and Housing/Box are recorded for context only, never set on the order.
		//
		// Both of their sections are hidden on a cover order (Lid and Add Ons are both gated on
		// isCover), so nothing here is a spec of what's being sold - a replacement cover carries no
		// lid and no housing. Housing especially must stay off the order: the polymer housing line
		// items key off unitLocation + addOns.housingType with no model type guard at all, so setting
		// it would silently price a housing the customer isn't buying, in a section they can't see.
		// Lid Type is ambiguous besides ("Flat Lid" is either "No Brackets" or "With Brackets").
		if (spec.lidType)
			notCarried.push(`Lid Type: ${spec.lidType}`);
		if (spec.housingType)
			notCarried.push(`Housing/Box: ${spec.housingType}`);

		// Measurements carry across as-is
		if (spec.widthFeet != null) specs.system.trackSpaceFeet = spec.widthFeet;
		if (spec.widthInches != null) specs.system.trackSpaceInches = spec.widthInches;
		if (spec.lengthFeet != null) specs.system.trackLengthFeet = spec.lengthFeet;
		if (spec.lengthInches != null) specs.system.trackLengthInches = spec.lengthInches;
		if (spec.widthFeet != null || spec.lengthFeet != null)
			carried.push("Width/Length");

		// The sheet only stores a slack the tech overrode. Otherwise it's left for the order's Slack field,
		// which works out the same standard value the sheet was showing from the width and track type.
		if (spec.slack != null) {
			specs.cover.slack = spec.slack;
			specs.cover.slackOverride = true;
			carried.push("Slack");
		}

		lathamOrderModel.officeNotes = this.buildOfficeNotes(spec, carried, notCarried, lathamOrderModel.officeNotes);

		return lathamOrderModel;
	}

	// Everything the tech captured that the order specs have no field for, plus anything recorded in a
	// vocabulary the order doesn't share, plus a header so it's obvious where the numbers came from.
	private buildOfficeNotes(spec: IAutoCoverSpecModel, carried: string[], notCarried: string[], existingNotes: string): string {
		const lines: string[] = ["Replacement cover quoted from the Auto Cover equipment tracker."];

		const manufacturer = spec.manufacturer === "Other" ? spec.manufacturerOther : spec.manufacturer;
		if (manufacturer || spec.model)
			lines.push(`Existing system: ${[manufacturer, spec.model].filter(x => !!x).join(" ")}`);

		// Stored in inches, shown the way the tech entered them: feet and inches
		const measure = (inches: number) => inches != null ? formatAutoCoverInches(inches) : "?";

		if (spec.housingLength != null || spec.housingWidth != null || spec.housingDepth != null)
			lines.push(`Housing size: ${measure(spec.housingLength)} L x ${measure(spec.housingWidth)} W x ${measure(spec.housingDepth)} D`);
		// No order field for these, but the office needs them to work out the track length
		if (spec.motorSideOffset != null || spec.nonMotorSideOffset != null)
			lines.push(`Track offset: ${measure(spec.motorSideOffset)} motor side, ${measure(spec.nonMotorSideOffset)} non-motor side`);
		if (spec.guideFeedType)
			lines.push(`Guide feeds: ${spec.guideFeedType}`);
		if (spec.waterFeature != null)
			lines.push(`Water feature: ${spec.waterFeature ? "Yes" : "No"}`);
		if (spec.installationDate)
			lines.push(`System installed: ${this.formatDate(spec.installationDate)}`);
		if (spec.lastCoverReplacementDate)
			lines.push(`Last cover replacement: ${this.formatDate(spec.lastCoverReplacementDate)}`);

		// Covers both kinds of leftover: a value with no exact order equivalent, and a value that is
		// context about the existing system rather than part of a replacement cover.
		if (notCarried.length) {
			lines.push("");
			lines.push("Recorded on site, not set on the order:");
			notCarried.forEach(x => lines.push(`  - ${x}`));
		}

		const block = lines.join("\n");

		return existingNotes ? `${existingNotes}\n\n${block}` : block;
	}

	private formatDate(value: Date | string): string {
		const date = value instanceof Date ? value : new Date(value);
		if (isNaN(date.getTime()))
			return "";

		return `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getDate().toString().padStart(2, "0")}/${date.getFullYear()}`;
	}
}
